// src/data/claimHowTo.js
//
// THE STEP-BY-STEP HOW-TO, PER AGREEMENT (Bob, 2026-10-15): "For every claimant and every case of discipline ... an
// obvious, accurate, and secure detailed how to complete the claim accurately, how to submit it properly in accordance
// with their cba." Then: "Build the per-agreement step-by-step how-to (complete the claim, submit it per the CBA),
// starting with the 6 agreements already catalogued, then discipline."
//
// THIS MODULE HOLDS NO FACT OF ITS OWN. Every number, officer, consequence and cite below is read from
// claimRequirements.js (claims) or disciplineAnswerLimits.js (discipline), each of which is a projection of a verbatim
// quote at a cited line in data\agreements\*. A second hand-typed copy would drift from the record (section XI); this
// one cannot, because it has none. Where the record holds nothing, the step says so - it never fills the gap.
// The description standard is Bob's own (ruling 265) and the facts line is ruling 261's (box1Guided.js).
// Pure: no imports beyond the record, no clock, no Firestore. Proved by scripts\test-claim-howto.mjs.
import { getClaimRequirements, canonicalAgreementKey, NO_REQUIREMENTS_TEXT } from './claimRequirements';
import { disciplineRecordFor, CONFIRMED as DISCIPLINE_CONFIRMED } from './disciplineAnswerLimits';
import { FACTS_LINE } from './box1Guided';

// Ruling 265, Bob's words (data\YCP_RULINGS_2026-09-29.md:276-278).
export const DESCRIPTION_STANDARD = 'Must remain clear, precise, honest, secure and accurate. No embellishing, no '
  + 'exaggeration, no lies, no inventing the truth, no stretching the truth, no supposition, no guessing. Just factually '
  + 'sourced accuracy when describing how or why this rule is violated.';

// "60 days" / "12 months" - a clock carrying `months` is stated in months, as the agreement states it.
export function period(clock) {
  if (clock && Number.isFinite(clock.months)) return clock.months + ' month' + (clock.months === 1 ? '' : 's');
  const d = clock && clock.days;
  return Number.isFinite(d) ? d + ' day' + (d === 1 ? '' : 's') : '';
}

function clock(req, id) { return (req.clocks || []).find((c) => c.id === id) || null; }
// "the date of the occurrence" -> "date of the occurrence", so "of the " + it never doubles the article.
const bare = (s) => String(s || '').replace(/^the /i, '');
// A quoted consequence keeps its own words; the sentence supplies the closing period.
const quoted = (s) => '"' + String(s || '').replace(/[.\s]+$/, '') + '."';

// ---- CLAIMS ------------------------------------------------------------------------------------------------------
// howToFile(agreementName, service?) -> { recorded:false, text } when the platform holds no claims rule for it,
// else { recorded:true, agreementKey, claimsRule, source, steps:[{ n, title, body:[lines], cite }] }.
// `service` is serviceFor()'s answer for this claim (the GC's setting or the agreement's default); null -> hand receipt.
export function howToFile(agreementName, service) {
  const req = getClaimRequirements(agreementName);
  if (!req) return { recorded: false, text: NO_REQUIREMENTS_TEXT };
  const steps = [];
  const add = (title, body, cite) => steps.push({ n: steps.length + 1, title, body: body.filter(Boolean), cite: cite || '' });

  const c1 = clock(req, 'C1');
  add('Know your deadline before you start',
    ['File within ' + req.filingWindowDays + ' days of the ' + bare((c1 && c1.from) || 'date of occurrence') + '. The date of occurrence counts as day one.',
      c1 && c1.onSilence ? 'If it is not filed in time, the agreement says: ' + quoted(c1.onSilence) : '',
      'The platform shows the exact date once you enter the date of occurrence. A late claim can still be filed - timeliness is your Local and General Chairperson\'s call, not the software\'s.'],
    c1 ? c1.cite : req.source);

  add('Fill in every item this agreement requires',
    req.payable.map((p) => '- ' + p.label),
    req.source);

  add('Say what happened - in your own words',
    [FACTS_LINE,
      'Cover who (the other employee, and who decided), what happened, when, where, and what you should have gotten.',
      'The standard every claim is held to: "' + DESCRIPTION_STANDARD + '"',
      'When you leave the description box, spelling and grammar are corrected where no name, date or number would change. Your words as you wrote them stay on the record.'],
    'Rulings 261 and 265');

  // The E2 note is shown only where it adds to the officer named (CSX Northern Lines); a bare cite repeats the source.
  const e2note = String((req.eligibility.find((e) => e.id === 'E2') || {}).note || '');
  add('Present it in writing to the right officer',
    ['Present the claim to ' + req.presentmentOfficer + '.', e2note.length > 60 ? e2note : ''],
    req.source);

  // No service passed: the agreement's own default (CSX), else the hand receipt - serviceFor()'s order.
  if (!service && req.service) service = { ...req.service, setBy: 'agreement' };
  const receipt = service && Object.prototype.hasOwnProperty.call(service, 'receipt') ? service.receipt : undefined;
  // Section 762 (ruling 325): how he delivers it is his choice among the ways his agreement allows.
  add('Choose how you deliver it',
    ['Hand delivery with a signed receipt, certified mail with a return receipt, or first-class mail are your options. Check your agreement\'s own rule on how a claim is presented - it governs.',
      'An e-mailed receipt may be challenged. Keep paper proof of delivery whenever you can.'],
    'Ruling 325');

  add('Get proof it was received',
    receipt === null
      ? ['Presented electronically. ' + ((service && service.note) || 'The transmittal and its confirmation are saved with the claim.')]
      : receipt
        ? ['The claim form carries the receipt line: "' + receipt.left + '" and "' + receipt.right + '". Fill it in when the claim is served, and record the service on the platform.']
        : ['The claim form carries a hand receipt: the officer who receives it signs it with the date and time. Record the service on the platform.',
          'Your General Chairperson may set a different method of service for this property.'],
    service && service.setBy === 'gc' ? 'Your General Chairperson\'s service setting' : (req.service ? req.source : 'Section 539: hand receipt unless your General Chairperson sets otherwise'));

  // Section 762 (ruling 325, Bob 2026-10-21 20:04 and 20:16 ET).
  add('Your Local and General Chairperson get it',
    ['When you print the finished claim, the platform sends it to your Local Chairperson and your General Chairperson and records that it did.',
      'You may also hand-deliver, mail or fax a copy to them yourself.'],
    'Ruling 325');

  const c2 = clock(req, 'C2');
  if (c2) {
    add('What the Carrier must do next',
      ['The Carrier has ' + period(c2) + ' from ' + c2.from + ' to answer in writing.',
        c2.onSilence ? 'If it does not, the agreement says: ' + quoted(c2.onSilence) + ' The platform watches this clock for you.' : ''],
      c2.cite);
  }

  const later = (req.clocks || []).filter((c) => c.id !== 'C1' && c.id !== 'C2');
  if (later.length || (req.ladder && req.ladder.length > 1)) {
    const lines = [];
    if (req.ladder && req.ladder.length > 1) {
      req.ladder.slice(1).forEach((r) => lines.push('Step ' + r.step + ': the ' + r.from + ' takes it to the ' + r.to + ' (' + r.cite + ').'));
    }
    later.forEach((c) => lines.push('- ' + c.label + ': ' + period(c) + ' from ' + c.from
      + (c.party === 'carrier' ? ' (the Carrier\'s clock)' : ' (our clock)')
      + (c.onSilence ? '. If missed: ' + quoted(c.onSilence).slice(0, -2) + '"' : '') + ' - ' + c.cite + '.'));
    lines.push('Appeals are handled by your Local and General Chairperson; the platform keeps every one of these dates.');
    add('If the claim is denied', lines, req.source);
  }

  if (Number.isFinite(req.retroactiveCapDays)) {
    add('A limit on back pay',
      ['One claim covers a continuing violation for as long as it continues, but no money is allowed for more than '
        + req.retroactiveCapDays + ' days before the claim is filed. File as soon as it starts.'],
      req.retroactiveCapCite);
  }

  // Section 762 (Bob 2026-10-21 19:53 ET): what to keep.
  add('Keep your records',
    ['Keep the signed claim, your proof of delivery (the signed receipt, the certified-mail card or the mailing slip), the Carrier\'s written answer and every letter after it.',
      'Upload each one to this claim\'s Documents so its date is kept with the claim.'],
    'Section 762');

  return { recorded: true, agreementKey: canonicalAgreementKey(agreementName), claimsRule: req.claimsRule, source: req.source, steps };
}

// ---- DISCIPLINE --------------------------------------------------------------------------------------------------
// howToDiscipline(agreementName) -> { recorded:false, text } or { recorded:true, confirmed, agreementKey, steps }.
// The discipline clocks are EXTRACTED-UNCONFIRMED (disciplineAnswerLimits.js CONFIRMED=false) - every answer carries
// `confirmed` and the screen must say so. A null clock is stated as not held, never filled.
export const NO_DISCIPLINE_TEXT = 'No discipline time limits are recorded on this platform for this agreement. That '
  + 'does not mean there are none - it means this platform does not hold them. Call your Local Chairperson at once; '
  + 'your General Chairperson holds authority over the agreement.';

export const UNCONFIRMED_TEXT = 'DRAFT - these time limits were read from the agreement but have not yet been '
  + 'confirmed by the platform\'s subject-matter expert. Check every date with your Local Chairperson.';

// audience: 'member' (the default) | 'officer'. A member cannot open a discipline case - New Case is an officer's
// door (roleScreens.js SCREENS.member has no 'new-case'; DisciplineCases.js hides the button) - so step 1 tells a
// member to give the letter to his Local Chairperson, and only an officer is told to open the case (audit 2026-10-16).
export function howToDiscipline(agreementName, audience) {
  const a = disciplineRecordFor(agreementName);
  if (!a) return { recorded: false, text: NO_DISCIPLINE_TEXT };
  const steps = [];
  const add = (title, body, cite) => steps.push({ n: steps.length + 1, title, body: body.filter(Boolean), cite: cite || '' });
  const n = (v) => (Number.isFinite(v) ? v : null);
  const cites = a.cites || {};
  const notHeld = 'not stated in the agreement text this platform holds - ask your Local Chairperson';

  if (audience === 'officer') {
    add('Bring the charge to the platform at once',
      ['Open a discipline case and upload the charge or notice letter exactly as it was received. Record the date it was received.',
        'The Local Chairperson works the case from here. Once it is forwarded, the General Chairperson has authority over it.'],
      'Discipline ladder (Record 611)');
  } else {
    add('Give the letter to your Local Chairperson at once',
      ['Give your Local Chairperson the charge or notice letter exactly as you received it. Tell him the date you received it.',
        'Your Local Chairperson opens and works the case. Once it is forwarded, your General Chairperson has authority over it.'],
      'Discipline ladder (Record 611)');
  }

  const from = a.from || {};
  const after = (k) => (from[k] ? ' of ' + from[k] : '');
  const carrier = [];
  if (n(a.noticeOfIntentDays) !== null) carrier.push((n(a.intentMeetingDays) !== null ? '- Letter of intent (discipline short of dismissal): within ' : '- Written notice: within ') + a.noticeOfIntentDays + ' days' + after('noticeOfIntent') + ' (' + (cites.noticeOfIntent || cites.notice) + ').');
  if (n(a.intentMeetingDays) !== null) carrier.push('- Intent meeting: within ' + a.intentMeetingDays + ' days of your receipt of the letter of intent (' + cites.intentMeeting + ').');
  carrier.push('- Hearing (investigation) begins: ' + (n(a.hearingCommencementDays) !== null
    ? 'within ' + a.hearingCommencementDays + ' days' + (from.hearing ? ' of ' + from.hearing
      : a.hearingFrom === 'first_knowledge' ? ' of the supervisor\'s first knowledge'
      : cites.scheduling ? ' of the date the notice is mailed' : '') + ' (' + (cites.hearing || cites.scheduling) + ').'
    : notHeld + '.'));
  carrier.push('- Decision after the hearing: ' + (n(a.decisionAfterHearingDays) !== null ? 'in writing within ' + a.decisionAfterHearingDays + ' days' + after('decision') + ' (' + cites.decision + ').' : notHeld + '.'));
  add('What the Carrier must do, and by when', carrier, a.provision);

  if (a.memberProtection) {
    add('Your protection if the Carrier misses', ['The agreement provides: ' + a.memberProtection + '.'],
      cites.managementNonAttendance || cites.noDisciplineIfLate || cites.heldOutPay || '');   // heldOutPay: forensic audit 2026-10-16 rename (Metra, Soo)
  }

  const appeal = [];
  if (a.appealRoutesToClaimArticle20) {
    appeal.push('An appeal runs on the claim time limits of this agreement (Article 20) - see the claim steps.');
  } else if (cites.dismissalExpedite && !cites.appealFiling) {
    // D&H/CP 13.7, read at source 2026-10-15 (dh-codified-2007.source.txt:790-798): discipline is grieved under
    // Article 14 from 14.1; only a DISMISSAL may be expedited, by the General Chairman within 60 days of the decision.
    appeal.push('- Discipline is grieved under the claim procedure (Article 14), started as a claim - see the claim steps.');
    appeal.push('- Dismissal: the General Chairperson may expedite it, appealing in writing to the highest appeals officer within '
      + a.appealFilingDays + ' days' + after('appealFiling') + ' (' + cites.dismissalExpedite + ').');
  } else {
    appeal.push('- Appeal in writing: ' + (n(a.appealFilingDays) !== null ? 'within ' + a.appealFilingDays + ' days' + after('appealFiling') : notHeld)
      + (cites.appealFiling ? ' (' + cites.appealFiling + ')' : '') + '.');
    if (n(a.appealDecisionDays) !== null) appeal.push('- The Carrier decides each appeal within ' + a.appealDecisionDays + ' days' + after('appealDecision') + ' (' + cites.appealDecision + ').');
    if (n(a.nonAcceptanceDays) !== null) appeal.push('- The last appeal decision becomes final and binding unless the Carrier is told in writing, within ' + a.nonAcceptanceDays + ' days of it, that it is not acceptable (' + cites.nonAcceptance + ').');
    if (n(a.boardInstitutionMonths) !== null) appeal.push('- Further handling must be started within ' + a.boardInstitutionMonths + ' months of that decision (' + cites.boardInstitution + ').');
  }
  appeal.push('Your Local and General Chairperson file the appeal; the platform keeps the dates.');
  add('If discipline is assessed - the appeal', appeal, a.provision);

  return { recorded: true, confirmed: DISCIPLINE_CONFIRMED === true, agreementKey: canonicalAgreementKey(agreementName), steps };
}
